import { useLanguageContext } from 'context/LanguageProvider';
import { useToastContext } from 'context/ToastContext';
import useLocale from 'hooks/useLocale';
import useLocalizedData from 'hooks/useLocalizedData';
import { useEffect, useState } from 'react';
import { fetchHeader, fetchLocales } from 'services/Strapi.service';
import { extractErrorMessage } from 'utils/axiosUtils';
import HEADER_LABELS from './Header.labels';

const useHeader = () => {
  const { language } = useLanguageContext();
  const locale = useLocale();
  const { showToast } = useToastContext();
  const [data, setData] = useState(null);
  const [locales, setLocales] = useState(null);
  const [currentLocale, setCurrentLocale] = useState(null);
  const [localesMenuAnchorElement, setLocalesMenuAnchorElement] = useState(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [labels] = useLocalizedData(HEADER_LABELS);

  useEffect(() => {
    fetchHeader(locale)
      .then(({ data }) =>
        setData(typeof data == 'object' && Array.isArray(data?.data) ? (data.data.at(0)?.attributes ?? null) : null)
      )
      .catch((err) => {
        showToast({ title: 'Header UI', message: extractErrorMessage(err) });
        setData(null);
      });
    fetchLocales(locale)
      .then(({ data }) =>
        setLocales(
          Array.isArray(data)
            ? data.map((it) => ({
                ...it,
                localeLabel: typeof it?.code === 'string' ? labels?.lang?.[it.code] : undefined,
              }))
            : null
        )
      )
      .catch((err) => {
        showToast({ title: 'Locals UI', message: extractErrorMessage(err) });
        setLocales(null);
      });
  }, [locale]);

  useEffect(() => {
    if (typeof language == 'string' && Array.isArray(locales)) {
      setCurrentLocale(
        locales.find(
          ({ code }) => typeof code == 'string' && code.trim().toLowerCase() === language.trim().toLowerCase()
        ) ?? null
      );
    } else {
      setCurrentLocale(null);
    }
  }, [language, locales]);

  const toggleMenu = () => {
    setMenuIsOpen(!menuIsOpen);
  };

  useEffect(() => {
    if (!menuIsOpen) {
      return;
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuIsOpen(false);
      }
    };
    document.body.classList.add('mobile-menu-open');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('mobile-menu-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuIsOpen]);

  return {
    labels: labels,
    data: data,
    locales: locales,
    currentLocale: currentLocale,
    localesMenuAnchorElement: localesMenuAnchorElement,
    setLocalesMenuAnchorElement: setLocalesMenuAnchorElement,
    menuIsOpen: menuIsOpen,
    toggleMenu: toggleMenu,
  };
};
export default useHeader;
